"use client";

import { Button } from "@/components/ui/button";
import DocumentUpload from "@/components/ui/document-upload";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth-context";
import { savePersonalInfo } from "@/services/clientAreaService";
import { formatCPFCNPJ, formatPhone } from "@/utils/format";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

export type PersonalFormValues = {
  personalInfo: {
    cli_razao_social: string;
    cli_celular: string;
    cli_email: string;
    cli_cnpj: string;
    cli_uf: string;
    cli_nacionalidade: string;
    cli_genero: string;
    cli_rg: string;
    cli_rg_validade: string;
  };
};

interface PersonalInfoFormProps {
  onSaved: (data: PersonalFormValues["personalInfo"]) => void;
  onCancel: () => void;
}

export default function PersonalInfoForm({
  onSaved,
  onCancel,
}: PersonalInfoFormProps) {
  const { user } = useAuth();
  const cliCodigo = user?.cli_codigo ?? "";
  const form = useFormContext<PersonalFormValues>();

  const [sending, setSending] = useState(false);

  const onSubmit = async (values: PersonalFormValues) => {
    if (!cliCodigo) return;
    setSending(true);
    try {
      await savePersonalInfo(cliCodigo, values.personalInfo);
      onSaved(values.personalInfo);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <FormField
        control={form.control}
        name="personalInfo.cli_razao_social"
        render={({ field }) => (
          <FormItem>
            <label className="block text-sm mb-1">Nome completo:</label>
            <FormControl>
              <Input placeholder="Nome completo:" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="personalInfo.cli_celular"
        render={({ field }) => (
          <FormItem>
            <label className="block text-sm mb-1">Telefone:</label>
            <FormControl>
              <Input
                placeholder="(11) 99999-9999"
                value={field.value}
                onChange={(e) => field.onChange(formatPhone(e.target.value))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-col">
        <label className="block text-sm mb-1">Código Cliente:</label>
        <Input value={cliCodigo} placeholder="Código Cliente:" disabled />
      </div>

      <FormField
        control={form.control}
        name="personalInfo.cli_email"
        render={({ field }) => (
          <FormItem>
            <label className="block text-sm mb-1">Email:</label>
            <FormControl>
              <Input type="email" placeholder="Seu email" {...field} disabled />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="personalInfo.cli_cnpj"
        render={({ field }) => (
          <FormItem>
            <label className="block text-sm mb-1">CPF/CNPJ:</label>
            <FormControl>
              <Input
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                value={field.value}
                onChange={(e) => field.onChange(formatCPFCNPJ(e.target.value))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <DocumentUpload sigla="CLI" registro={cliCodigo} />

      <div className="pt-4 flex justify-end gap-4">
        <Button type="button" variant="border" size="small" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={sending} size="small">
          {sending ? "Atualizando..." : "Atualizar"}
        </Button>
      </div>
    </form>
  );
}
